import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from '@/hooks/notifications/useWebSocket';
import { useLoggedUser } from '@/hooks/user/useLoggedUser';

interface NotificationContextProps {
  isConnected: boolean;
  unreadCount: number;
  setUnreadCount: (count: number) => void;
}

const NotificationContext = createContext<NotificationContextProps | null>(
  null,
);

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const { userId } = useLoggedUser();
  const { isConnected } = useWebSocket(); // This will initialize the WebSocket connection
  const queryClient = useQueryClient();
  const [unreadCount, setUnreadCount] = useState(0);

  // Update unread count when WebSocket receives a notification
  useEffect(() => {
    if (isConnected && userId) {
      // Invalidate and refetch the unread count when a new notification arrives
      // This will be triggered by the WebSocket notification handler
      const handleNewNotification = () => {
        queryClient.invalidateQueries({
          queryKey: ['notifications', 'unread-count'],
        });
      };

      // Listen for custom events that indicate new notifications
      window.addEventListener('new-notification', handleNewNotification);

      return () => {
        window.removeEventListener('new-notification', handleNewNotification);
      };
    }
  }, [isConnected, userId, queryClient]);

  return (
    <NotificationContext.Provider
      value={{
        isConnected,
        unreadCount,
        setUnreadCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotificationContext must be used within a NotificationProvider',
    );
  }
  return context;
};
